<div class="flex h-full min-h-screen flex-col bg-white">
  <div class="mx-auto flex h-full w-full max-w-7xl flex-col p-6">
    <!-- Header -->
    <div class="mb-3">
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-lg border border-sky-100 bg-sky-50">
          <dojo-icon [icon]="'wallet'" [size]="20" class="text-sky-600" />
        </div>
        <div>
          <h1 class="text-xl font-semibold text-gray-900">Payment Center</h1>
          <p class="text-sm text-gray-500">Manage your payout account and payments</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    @if (state.isLoading) {
      <div class="flex items-center justify-center py-16">
        <div class="flex items-center gap-3">
          <div class="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600"></div>
          <span class="text-sm text-gray-600">Loading payment information...</span>
        </div>
      </div>
    }

    <!-- Error State -->
    @if (state.error && state.isLoading === false) {
      <div class="mb-4 rounded-xl border border-red-200 bg-red-50 p-6">
        <div class="flex items-start gap-4">
          <div class="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <dojo-icon [icon]="'exclamation-triangle'" [size]="20" class="text-red-600" />
          </div>
          <div class="flex-1">
            <h3 class="font-medium text-red-900">Error loading payment information</h3>
            <p class="mt-1 text-sm text-red-700">An unexpected error occurred while loading your payment data.</p>
          </div>
        </div>
      </div>
    }

    <!-- Content -->
    @if (state.isLoading === false) {
      <!-- No Payout Account Warning -->
      @if (state.hasPayoutAccount === false) {
        <div class="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-6">
          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <dojo-icon [icon]="'exclamation-triangle'" [size]="20" class="text-amber-600" />
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-amber-900">Payout Account Required</h3>
              <p class="mt-2 text-sm text-amber-800">
                Payouts will be made via Wise. If you have an existing Wise account you should use this email here. If
                not please use the email where you would like to receive the payout email.
                <strong>You do not need to create a Wise account if you don't have one.</strong>
                Payouts can be made directly to your bank account.
              </p>
              <div class="mt-6 space-y-4">
                <div class="space-y-4">
                  <!-- Email Field -->
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">Email Address</label>
                    <dojo-text-input
                      [formCtrl]="emailControl"
                      type="email"
                      placeholder="your-email@example.com"
                      autocomplete="email"
                    />
                  </div>

                  <!-- Name Field -->
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">Recipient Name</label>
                    <dojo-text-input
                      [formCtrl]="nameControl"
                      type="text"
                      placeholder="Enter your full name"
                      autocomplete="name"
                    />
                  </div>

                  <!-- Currency Field -->
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">Currency</label>
                    <dojo-single-select
                      [formCtrl]="currencyControl"
                      [options]="state.currencies"
                      [optionValue]="'code'"
                      [optionLabel]="'name'"
                      placeholder="Select currency"
                    />
                  </div>

                  <!-- Submit Button -->
                  <div class="flex justify-end">
                    <dojo-button
                      [fill]="'solid'"
                      [size]="'s'"
                      [disabled]="!payoutForm.valid || state.isLoading"
                      (click)="setupPayoutAccount()"
                    >
                      @if (state.isLoading) {
                        <div
                          class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                        ></div>
                        Creating Account...
                      } @else {
                        <dojo-icon [icon]="'plus'" class="mr-2" />
                        Set Up Payout Account
                      }
                    </dojo-button>
                  </div>

                  <!-- Account Creation Error -->
                  @if (state.error && state.hasPayoutAccount === false) {
                    <div class="rounded-lg border border-red-200 bg-red-50 p-3">
                      <div class="flex items-start gap-3">
                        <dojo-icon [icon]="'exclamation-triangle'" [size]="16" class="mt-0.5 text-red-600" />
                        <div class="flex-1">
                          <p class="text-sm font-medium text-red-800">Failed to create payout account</p>
                          <p class="mt-1 text-xs text-red-700">
                            Please check your email address and try again. If the problem persists, contact support.
                          </p>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- General Setup Section -->
      @if (state.payoutAccount; as payoutAccount) {
        <div class="mb-5 mt-5">
          <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">General Setup</h2>
        </div>

        <!-- Payout Account and Hourly Rate Section -->
        <div class="mb-6 grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          <!-- Payout Account Panel -->
          <div>
            <mat-accordion>
              <mat-expansion-panel
                class="payout-account-panel mat-elevation-z0"
                [expanded]="!payoutAccount.isSetupComplete"
              >
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <div class="flex w-full items-center justify-between pr-4">
                      <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">Payout Account</h2>
                      @if (state.isLoading) {
                        <span
                          class="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1.5 text-sm font-medium text-sky-800"
                        >
                          <div
                            class="h-2 w-2 animate-spin rounded-full border border-sky-500 border-t-transparent"
                          ></div>
                          Pending
                        </span>
                      } @else if (payoutAccount.isSetupComplete) {
                        <span
                          class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 text-sm font-medium text-green-800"
                        >
                          <div class="h-2 w-2 rounded-full bg-green-500"></div>
                          Setup Complete
                        </span>
                      } @else {
                        <span
                          class="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1.5 text-sm font-medium text-yellow-800"
                        >
                          <div class="h-2 w-2 rounded-full bg-yellow-500"></div>
                          Pending Setup
                        </span>
                      }
                    </div>
                  </mat-panel-title>
                </mat-expansion-panel-header>

                @if (!payoutAccount.isSetupComplete) {
                  <div
                    class="mb-4 mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20"
                  >
                    <p class="text-sm text-yellow-800 dark:text-yellow-200">
                      You have successfully added your payout account. You can now submit payout requests.
                    </p>
                  </div>
                }

                <!-- Content -->
                <div class="grid grid-cols-1 gap-6 md:grid-cols-3" [class.mt-6]="payoutAccount.isSetupComplete">
                  <div>
                    <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >Recipient Email</label
                    >
                    <p class="text-sm text-gray-900 dark:text-gray-100">{{ payoutAccount.recipient_email }}</p>
                  </div>

                  <div>
                    <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >Recipient Name</label
                    >
                    <p class="text-sm text-gray-900 dark:text-gray-100">{{ payoutAccount.recipient_name }}</p>
                  </div>

                  <div>
                    <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Currency</label>
                    <p
                      class="inline-block rounded bg-gray-50 px-2 py-1 font-mono text-sm text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                    >
                      {{ payoutAccount.recipient_currency }}
                    </p>
                  </div>
                </div>

                <div class="mt-3 flex gap-3">
                  <dojo-button [fill]="'outline'" [size]="'s'" (click)="editPayoutAccount()">
                    <dojo-icon [icon]="'pencil'" class="mr-2" />
                    Edit Account
                  </dojo-button>
                </div>
              </mat-expansion-panel>
            </mat-accordion>
          </div>

          <!-- Hourly Rate Panel -->
          <div>
            <mat-accordion>
              <mat-expansion-panel class="payout-account-panel mat-elevation-z0" [expanded]="false">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <div class="flex w-full items-center justify-between pr-4">
                      <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">My Hourly Rate</h2>
                      @if (state.hourlyRate !== null) {
                        <span
                          class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 text-sm font-medium text-green-800"
                        >
                          <div class="h-2 w-2 rounded-full bg-green-500"></div>
                          Setup Complete
                        </span>
                      } @else {
                        <span
                          class="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1.5 text-sm font-medium text-yellow-800"
                        >
                          <div class="h-2 w-2 rounded-full bg-yellow-500"></div>
                          Not Set
                        </span>
                      }
                    </div>
                  </mat-panel-title>
                </mat-expansion-panel-header>

                @if (state.hourlyRate === null) {
                  <div class="mb-4 mt-6">
                    <p class="text-sm text-gray-700 dark:text-gray-300">
                      Your hourly payout rate has not been configured yet. Please contact an administrator to set your
                      rate before submitting payout requests.
                    </p>
                  </div>
                } @else {
                  <div class="mt-6">
                    <div class="flex items-center justify-between">
                      <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        {{ state.hourlyRate | currency: 'EUR' }}/hour
                      </p>
                      <dojo-button [fill]="'clear'" [size]="'xs'" (click)="viewRateHistory()">
                        <dojo-icon [icon]="'clock-rotate-left'" class="mr-1.5" [size]="14" />
                        View History
                      </dojo-button>
                    </div>
                  </div>
                }
              </mat-expansion-panel>
            </mat-accordion>
          </div>
        </div>
      }

      @if (state.hasPayoutAccount && state.hourlyRate !== null) {
        <div class="mb-6">
          <hr class="border-gray-200 dark:border-gray-700" />
        </div>
        <div class="flex flex-1 flex-col">
          <dojo-payout-requests />
        </div>
      }
    }
  </div>
</div>
